import { createColumnHelper } from '@tanstack/react-table';
import { LocalizedText, LocalizedDate, DateTimeService } from '@mews/framework';
import { Dash, LocalizedDateTimeFromUtc } from '@mews-commander/ui';
import { Link } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { Payout, PayoutDetailResult } from '@mews-commander/data-access';
import { PAYOUT_TRANSACTION_TYPE_TEXT_KEY_MAP } from '../textKeyMaps';
import { getLocalizedCurrencyValue } from '../utils';
import { PayoutStateTag } from './PayoutStateTag';

const payoutReportColumnHelper = createColumnHelper<Payout>();
const payoutDetailColumnHelper = createColumnHelper<PayoutDetailResult>();

export const getPayoutReportColumns = ({ handleLinkClick }: { handleLinkClick: (id: string) => void }) => [
    payoutReportColumnHelper.accessor('reportingIdentifier', {
        header: () => <LocalizedText textKey={TextKey.Identifier} />,
        cell: (props) => (
            <Link inherit onClick={() => handleLinkClick(props.row.id)}>
                {props.getValue()}
            </Link>
        ),
    }),
    payoutReportColumnHelper.accessor('status', {
        header: () => <LocalizedText textKey={TextKey.Status} />,
        cell: (props) => {
            const value = props.getValue();
            return value ? <PayoutStateTag state={value} /> : <Dash />;
        },
    }),
    payoutReportColumnHelper.accessor('net', {
        header: () => <LocalizedText textKey={TextKey.PayoutNet} />,
        cell: (props) => getLocalizedCurrencyValue(props.getValue()),
        meta: {
            align: 'end',
        },
    }),
    payoutReportColumnHelper.accessor('commission', {
        header: () => <LocalizedText textKey={TextKey.PayoutCommission} />,
        cell: (props) => getLocalizedCurrencyValue(props.getValue()),
        meta: {
            align: 'end',
        },
    }),
    payoutReportColumnHelper.accessor('gross', {
        header: () => <LocalizedText textKey={TextKey.PayoutGross} />,
        cell: (props) => getLocalizedCurrencyValue(props.getValue()),
        meta: {
            align: 'end',
        },
    }),
    payoutReportColumnHelper.accessor('createdUtc', {
        header: () => <LocalizedText textKey={TextKey.PayoutCreated} />,
        cell: (props) => <LocalizedDateTimeFromUtc utcString={props.getValue()} />,
    }),
    payoutReportColumnHelper.accessor('estimatedArrivalDate', {
        header: () => <LocalizedText textKey={TextKey.EstimatedArrival} />,
        cell: (props) => {
            const value = props.getValue();
            return value ? <LocalizedDate date={DateTimeService.fromLocalUtcString(value)} /> : <Dash />;
        },
    }),
];

export const getPayoutDetailColumns = ({ openSidePanel = () => {} }: { openSidePanel?: (id: string) => void }) => [
    payoutDetailColumnHelper.accessor('type', {
        header: () => <LocalizedText textKey={TextKey.Type} />,
        cell: (props) => <LocalizedText textKey={PAYOUT_TRANSACTION_TYPE_TEXT_KEY_MAP[props.getValue()]} />,
    }),
    payoutDetailColumnHelper.accessor('net', {
        header: () => <LocalizedText textKey={TextKey.PayoutNet} />,
        cell: (props) => getLocalizedCurrencyValue(props.getValue()),
        meta: {
            align: 'end',
        },
    }),
    payoutDetailColumnHelper.accessor('commission', {
        header: () => <LocalizedText textKey={TextKey.PayoutCommission} />,
        cell: (props) => getLocalizedCurrencyValue(props.getValue()),
        meta: {
            align: 'end',
        },
    }),
    payoutDetailColumnHelper.accessor('gross', {
        header: () => <LocalizedText textKey={TextKey.PayoutGross} />,
        cell: (props) => getLocalizedCurrencyValue(props.getValue()),
        meta: {
            align: 'end',
        },
    }),
    payoutDetailColumnHelper.accessor('transactionId', {
        header: () => <LocalizedText textKey={TextKey.Identifier} />,
        cell: (props) =>
            props.getValue() ? (
                <Link data-test-id="payout-payment-detail-open" inherit onClick={() => openSidePanel(props.getValue())}>
                    {props.row.id}
                </Link>
            ) : (
                props.row.id || <Dash />
            ),
    }),
    payoutDetailColumnHelper.accessor('createdUtc', {
        header: () => <LocalizedText textKey={TextKey.PayoutCreated} />,
        cell: (props) => <LocalizedDateTimeFromUtc utcString={props.getValue()} />,
    }),
];
